import { useState, useCallback } from 'react'
import { FolderOpen, Loader2, FolderX } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  saveWorkspaceHandleRecord,
  queryHandlePermission,
  requestHandlePermission,
  isFileSystemAccessSupported,
} from '@/infrastructure/storage/handles-db'
import { setWorkspaceRoot } from '@/infrastructure/storage/workspace-fs/root'
import { bootstrapWorkspace } from '@/infrastructure/storage/workspace-fs/bootstrap'

/**
 * 工作空间选择弹窗
 *
 * 在 /app/* 等非 WorkspaceGate 保护的路由中，让用户直接选择工作空间文件夹，
 * 无需跳转到「项目」页面。选择后会保存句柄到 handles-db 并设置 in-memory root。
 */
export function WorkspacePickerDialog({
  open,
  onOpenChange,
  onSuccess,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const supported = isFileSystemAccessSupported()

  const handlePick = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      // 1. 弹出原生文件夹选择器
      const handle = await window.showDirectoryPicker({
        id: 'freecut-workspace',
        mode: 'readwrite',
        startIn: 'documents',
      })

      // 2. 检查/请求读写权限
      const queryState = await queryHandlePermission(handle)
      const finalState =
        queryState === 'granted' ? queryState : await requestHandlePermission(handle)
      if (finalState !== 'granted') {
        setError('未授予文件夹访问权限，请重新选择并允许访问。')
        return
      }

      // 3. 持久化句柄到 handles-db
      await saveWorkspaceHandleRecord(handle)

      // 4. 设置 in-memory workspace root
      setWorkspaceRoot(handle)

      // 5. 非阻塞地初始化工作空间目录结构
      setTimeout(() => {
        void bootstrapWorkspace(handle)
      }, 0)

      onOpenChange(false)
      onSuccess?.()
    } catch (err) {
      // 用户取消选择器（AbortError）→ 静默，不显示错误
      if (err instanceof DOMException && err.name === 'AbortError') {
        return
      }
      setError(err instanceof Error ? err.message : '选择工作空间失败')
    } finally {
      setBusy(false)
    }
  }, [onOpenChange, onSuccess])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>选择工作区文件夹</DialogTitle>
          <DialogDescription>
            编辑片段需要先选择一个本地文件夹作为工作区。项目、素材和缓存都会以普通文件的形式保存在这里。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {!supported ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-destructive">
                <FolderX className="size-5" />
                <span className="font-medium">当前浏览器暂不支持</span>
              </div>
              <p className="text-sm text-muted-foreground">
                快剪依赖 Chromium 内核浏览器提供的文件系统访问能力。建议使用 Chrome、Edge、Brave 或 Arc。
              </p>
            </div>
          ) : (
            <>
              {error ? (
                <p className="text-sm text-destructive">{error}</p>
              ) : null}
              <Button
                className="w-full gap-2"
                onClick={handlePick}
                disabled={busy}
              >
                {busy ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <FolderOpen className="size-4" />
                )}
                {busy ? '正在选择…' : '选择文件夹'}
              </Button>
              <p className="text-xs text-muted-foreground">
                提示：可以把工作区放在 Dropbox、iCloud 或 Google Drive 之类的同步目录中，方便多设备同步。
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
